/**
 * Bring the map to a plan overlay (owner, 2026-09-27: selecting a plan row
 * should fly to it — the first production smoke lost twenty minutes hunting
 * for a sheet placed far from the site's field).
 *
 * The overlay quad is [TL, TR, BR, BL] in lng/lat; we fit its bounding box.
 */
import type { Map as MapboxMap } from 'mapbox-gl';
import type { Overlay } from '../stores/projectStore';

export type LngLatBox = [[number, number], [number, number]];

/** Bounding box of the overlay quad, or null when the quad is unusable
 *  (fewer than two finite corners, or all corners on one point). */
export function overlayLngLatBounds(bounds: Overlay['bounds'] | undefined): LngLatBox | null {
  const pts = (bounds ?? []).filter(
    (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  );
  if (pts.length < 2) return null;
  const lngs = pts.map((p) => p[0]);
  const lats = pts.map((p) => p[1]);
  const box: LngLatBox = [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
  if (box[0][0] === box[1][0] && box[0][1] === box[1][1]) return null;
  return box;
}

/** Fit the map to the overlay. Returns false when there was nothing to fit
 *  (no map yet, or a degenerate quad) so callers can stay silent. */
export function fitMapToOverlay(
  map: MapboxMap | null | undefined,
  overlay: Pick<Overlay, 'bounds'>,
  opts: { padding?: number; duration?: number } = {},
): boolean {
  const box = overlayLngLatBounds(overlay.bounds);
  if (!map || !box) return false;
  map.fitBounds(box, { padding: opts.padding ?? 60, duration: opts.duration ?? 600 });
  return true;
}
